import { useCallback, useEffect, useState } from 'react';
import { batchApi, quizApi, examLibraryApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import { Button, Card, PageSkeleton, ErrorState, EmptyState, PageHeader } from '../components/ui';
import BatchDialog from '../components/dialogs/BatchDialog';

export default function BatchesPage() {
  const org = useOrg();
  const toast = useToast();
  const [batches, setBatches] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savingExamFor, setSavingExamFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([batchApi.list(org.id), quizApi.list(org.id), examLibraryApi.enrolled(org.id)])
      .then(([batchesRes, quizzesRes, examsRes]) => {
        setBatches(batchesRes.data || []);
        setQuizzes(quizzesRes.data || []);
        setExams(examsRes.data || []);
        setError(null);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load batches')))
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const examTitle = (examId) => exams.find((x) => x.examId === examId)?.title;

  // Batches created before the exam field existed can be given one here rather
  // than being recreated.
  const setBatchExam = async (batch, examId) => {
    if (!examId) return;
    setSavingExamFor(batch.id);
    try {
      await batchApi.update(org.id, batch.id, { examId });
      setBatches((items) => items.map((b) => (b.id === batch.id ? { ...b, examId } : b)));
      toast.success(`${batch.name} now prepares for ${examTitle(examId) || 'this exam'}.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to set the batch exam'));
    } finally {
      setSavingExamFor(null);
    }
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;

  return (
    <>
      <PageHeader
        eyebrow="Batch management"
        title="Batches"
        subtitle="A batch sets the exam its students prepare for, and is how you target quizzes and content."
        action={<Button onClick={() => setDialogOpen(true)}>+ Create batch</Button>}
      />
      <Card className="table-card">
        <table>
          <thead><tr><th>Batch</th><th>Exam</th><th>Students</th><th>Scheduled quizzes</th><th>Created</th></tr></thead>
          <tbody>
            {batches.length ? batches.map((b) => (
              <tr key={b.id}>
                <td><b>{b.name}</b></td>
                <td>
                  {b.examId ? (examTitle(b.examId) || b.examId) : (
                    <select
                      value=""
                      disabled={savingExamFor === b.id || !exams.length}
                      onChange={(e) => setBatchExam(b, e.target.value)}
                    >
                      <option value="">Set exam…</option>
                      {exams.map((x) => <option key={x.examId} value={x.examId}>{x.title}</option>)}
                    </select>
                  )}
                </td>
                <td>{b.studentCount}</td>
                <td>{quizzes.filter((q) => q.batchId === b.id).length}</td>
                <td>{b.createdAt ? new Date(b.createdAt).toLocaleDateString() : '—'}</td>
              </tr>
            )) : (
              <tr><td colSpan="5">
                <EmptyState title="No batches yet" action={<Button variant="secondary" onClick={() => setDialogOpen(true)}>Create your first batch</Button>}>
                  Batches let you organise students and target quizzes or content to the right group.
                </EmptyState>
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
      {dialogOpen && (
        <BatchDialog
          exams={exams}
          onClose={() => setDialogOpen(false)}
          onCreated={() => { setDialogOpen(false); load(); }}
        />
      )}
    </>
  );
}
